import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { AddVacancyModal } from '../components/domain/AddVacancyModal.js';
import { DeployModal } from '../components/domain/DeployModal.js';
import { WaIcon } from '../components/primitives/WaIcon.js';
import { downloadCSV } from '../lib/csv.js';
import { toast } from '../lib/toast.js';
import { waLink } from '../lib/wa.js';

interface Candidate {
  rank: number;
  score: number;
  status: 'available' | 'deployed' | 'unavailable';
  workerId: string;
  workerName: string;
  workerRole: string | null;
  workerRoles: string[];
  workerPostcode: string | null;
  workerPhone: string | null;
  onSiteUntil: string | null;
  distanceKm: number;
  matchingCerts: string[];
  missingRequiredSkills?: string[];
  details: string[];
}

interface MatchDiagnostics {
  totalWorkers: number;
  wrongRole: number;
  missingRequiredSkills: Record<string, string[]>;
}

interface Vacancy {
  id: string;
  code: string;
  projectId: string;
  projectName: string | null;
  projectCode?: string | null;
  role: string;
  startDate: string;
  duration: string;
  status: 'open' | 'filled';
  filledAt?: string | null;
  filledByName: string | null;
  requiredSkills: string[];
  requesterName: string | null;
  requesterEmail: string | null;
  requesterPhone: string | null;
  requesterRole: string | null;
  requesterCustomer: string | null;
  candidates: Candidate[];
  unavailableCandidates?: Candidate[];
  diagnostics?: MatchDiagnostics;
  requesterId?: string | null;
  statusFilter?: string[];
  notes?: string | null;
}

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

function avEl(name: string, size = 34, font = 12): JSX.Element {
  return (
    <div className="av" style={{ width: size, height: size, minWidth: size, fontSize: font, background: 'var(--accent)' }}>
      {initials(name)}
    </div>
  );
}

const STATUS_BADGE_CLASS: Record<string, string> = { available: 'b-avail', deployed: 'b-deployed', unavailable: 'b-unavail', dnu: 'b-dnu' };
function stBdg(status: string): JSX.Element {
  const label = status === 'dnu' ? 'DNU' : status;
  return <span className={`badge ${STATUS_BADGE_CLASS[status] ?? ''}`}>{label}</span>;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function Vacancy() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canExport, canCreateVacancy, canDeploy } = useRolePerms();
  const [activeId, setActiveId] = useState<string | null>(searchParams.get('id'));
  const [addOpen, setAddOpen] = useState(searchParams.get('new') === '1' && canCreateVacancy);
  const [editingVacancyId, setEditingVacancyId] = useState<string | null>(null);
  const [deployTarget, setDeployTarget] = useState<{ workerId: string; workerName: string; workerPhone: string; projectId: string; projectName: string; vacancyId: string } | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) setActiveId(id);
    if (searchParams.get('new') === '1') {
      setAddOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: vac = { open: [], filled: [] } } = useQuery({
    queryKey: ['dashboard', 'active-vacancies'],
    queryFn: () => api.get<{ open: Vacancy[]; filled: Vacancy[] }>('/dashboard/active-vacancies').then((r) => r.data),
  });

  const active = activeId ? vac.open.find((v) => v.id === activeId) ?? vac.filled.find((v) => v.id === activeId) ?? null : null;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['dashboard', 'active-vacancies'] });

  const markFilled = useMutation({
    mutationFn: (id: string) => api.post(`/vacancies/${id}/mark-filled`, { filledBy: null }),
    onSuccess: (_d, id) => { invalidate(); if (activeId === id) setActiveId(null); toast('Vacancy filled', '', 'success'); },
    onError: (e: any) => toast('Mark filled failed', e?.response?.data?.message ?? '', 'danger'),
  });
  const reopen = useMutation({
    mutationFn: (id: string) => api.post(`/vacancies/${id}/reopen`),
    onSuccess: () => { invalidate(); toast('Vacancy reopened', '', 'info'); },
    onError: (e: any) => toast('Reopen failed', e?.response?.data?.message ?? '', 'danger'),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/vacancies/${id}`),
    onSuccess: (_d, id) => { invalidate(); if (activeId === id) setActiveId(null); },
    onError: (e: any) => toast('Delete failed', e?.response?.data?.message ?? '', 'danger'),
  });
  const rerun = useMutation({
    mutationFn: (id: string) => api.post(`/vacancies/${id}/rerun`),
    onSuccess: () => { invalidate(); toast('Match re-run', 'Latest worker availability applied', 'info'); },
    onError: (e: any) => toast('Rerun failed', e?.response?.data?.message ?? '', 'danger'),
  });

  function confirmDelete(id: string) {
    if (window.confirm('Delete this vacancy permanently?')) del.mutate(id);
  }

  return (
    <div className="page" id="page-vacancy">
      <div className="hbar">
        <div>
          <div className="ph">Vacancy Matcher</div>
          <div className="ps">Find the best available worker for a role and location</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {canExport ? <button
            className="btn btn-ghost btn-sm"
            title={active ? 'Export the candidate list for the open vacancy' : 'Export all vacancy searches'}
            onClick={() => {
              if (active) {
                const candidateRow = (w: Candidate, rank: number | '') => ({
                  rank,
                  name: w.workerName,
                  role: (w.workerRoles && w.workerRoles.length ? w.workerRoles : [w.workerRole]).filter(Boolean).join('; '),
                  status: (w.status as string) === 'dnu' ? 'DNU' : w.status,
                  matchPct: w.score,
                  distanceKm: w.distanceKm.toFixed(1),
                  postcode: w.workerPostcode ?? '',
                  phone: w.workerPhone ?? '',
                  matchingCerts: (w.matchingCerts ?? []).join('; '),
                  notes: (w.details ?? []).join(' · '),
                });
                downloadCSV(
                  `vacancy-${active.code}-candidates.csv`,
                  [
                    ...active.candidates.map((w, i) => candidateRow(w, i + 1)),
                    ...(active.unavailableCandidates ?? []).map((w) => candidateRow(w, '')),
                  ],
                  [
                    { key: 'rank', label: 'Rank' },
                    { key: 'name', label: 'Name' },
                    { key: 'role', label: 'Role' },
                    { key: 'status', label: 'Status' },
                    { key: 'matchPct', label: 'Match %' },
                    { key: 'distanceKm', label: 'Distance (km)' },
                    { key: 'postcode', label: 'Postcode' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'matchingCerts', label: 'Matching Certs' },
                    { key: 'notes', label: 'Notes' },
                  ],
                );
                return;
              }
              downloadCSV('vacancies.csv', [...vac.open, ...vac.filled].map((v: any) => ({
                code: v.code,
                project: v.projectName ?? v.projectId,
                role: v.role,
                startDate: v.startDate?.slice(0, 10) ?? '',
                duration: v.duration,
                status: v.status,
                filledBy: v.filledByName ?? '',
                requester: v.requesterName ?? '',
                requiredSkills: (v.requiredSkills ?? []).join('; '),
                candidatesFound: v.candidates?.length ?? 0,
              })));
            }}
          >⬇ Export</button> : null}
          {canCreateVacancy ? <button className="btn btn-primary" onClick={() => setAddOpen(true)}>+ New Vacancy Search</button> : null}
        </div>
      </div>
      <div className="g2" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="sec-title">Active Vacancy Searches</div>
          <div id="vac-list">
            {vac.open.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)' }}>
                <div style={{ fontSize: 28, marginBottom: 9 }}>⚑</div>
                <div style={{ fontSize: 13 }}>No open vacancies.</div>
              </div>
            ) : (
              vac.open.map((v) => {
                const projLine = `${v.projectCode ?? v.projectId} — ${v.projectName ?? ''}`;
                return (
                  <div key={v.id} className="vac-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 12 }}>{v.role}</div>
                        <div style={{ fontSize: 11, color: 'var(--accent)' }}>{projLine}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>Start: {v.startDate.slice(0, 10)} · {v.duration}</div>
                        {v.requesterName ? (
                          <div style={{ fontSize: 10, color: 'var(--info)', marginTop: 2 }}>
                            👤 Requested by {v.requesterName}{v.requesterCustomer ? ` · ${v.requesterCustomer}` : ''}
                          </div>
                        ) : null}
                        {v.requiredSkills && v.requiredSkills.length ? (
                          <div style={{ fontSize: 9, color: 'var(--info)', marginTop: 2 }}>Skills: {v.requiredSkills.join(', ')}</div>
                        ) : null}
                      </div>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button className="btn btn-info btn-xs" onClick={() => setActiveId(v.id)}>Results</button>
                        {canCreateVacancy ? <button className="btn btn-ghost btn-xs" title="Edit this vacancy — role, dates, skills" onClick={() => setEditingVacancyId(v.id)}>✏ Edit</button> : null}
                        {canCreateVacancy ? <button className="btn btn-primary btn-xs" title="Mark this vacancy as filled (use when deployment happened outside the matcher)" onClick={() => markFilled.mutate(v.id)} disabled={markFilled.isPending}>✓ Mark Filled</button> : null}
                        {canCreateVacancy ? <button className="btn btn-danger btn-xs" title="Delete permanently" onClick={() => confirmDelete(v.id)} disabled={del.isPending}>✕</button> : null}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {vac.filled.length > 0 ? (
              <details style={{ marginTop: 16, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10 }}>
                <summary style={{ cursor: 'pointer', padding: '10px 14px', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--live)', listStyle: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>✓ Filled Vacancies ({vac.filled.length})</span>
                  <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>click to expand</span>
                </summary>
                <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {vac.filled.map((v) => {
                    const projLine = `${v.projectCode ?? v.projectId} — ${v.projectName ?? ''}`;
                    const filledAt = fmtDate(v.filledAt);
                    return (
                      <div key={v.id} className="vac-card" style={{ opacity: 0.75, borderColor: 'rgba(34,197,94,.3)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 12, textDecoration: 'line-through', color: 'var(--muted)' }}>{v.role}</span>
                              <span className="badge" style={{ background: 'rgba(34,197,94,.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,.35)', fontSize: 9, fontWeight: 700, padding: '1px 6px' }}>✓ FILLED</span>
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{projLine}</div>
                            {v.filledByName ? (
                              <div style={{ fontSize: 10, color: 'var(--live)', marginTop: 2 }}>
                                Filled by <strong>{v.filledByName}</strong>{filledAt ? ` on ${filledAt}` : ''}
                              </div>
                            ) : null}
                          </div>
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {canCreateVacancy ? <button className="btn btn-ghost btn-xs" title="Reopen this vacancy" onClick={() => reopen.mutate(v.id)} disabled={reopen.isPending}>↺ Reopen</button> : null}
                            {canCreateVacancy ? <button className="btn btn-danger btn-xs" title="Delete permanently" onClick={() => confirmDelete(v.id)} disabled={del.isPending}>✕</button> : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            ) : null}
          </div>
        </div>
        <div id="vac-results-panel" style={{ display: active ? 'block' : 'none' }}>
          <div className="card">
            <div id="vac-results">
              {active ? (() => {
                const projLine = `${active.projectCode ?? active.projectId} — ${active.projectName ?? ''}`;
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 13 }}>Best Matches{active.candidates.length ? ` (${active.candidates.length})` : ''}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                          {active.role} · {active.projectName ?? active.projectId} · {active.startDate.slice(0, 10)} · {active.duration}
                        </div>
                        {active.requiredSkills && active.requiredSkills.length ? (
                          <div style={{ fontSize: 10, color: 'var(--info)', marginTop: 2 }}>Required skills: {active.requiredSkills.join(', ')}</div>
                        ) : null}
                        {active.requesterName ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5, padding: '6px 10px', background: 'var(--info-dim)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 7, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 10, color: 'var(--info)', fontWeight: 600 }}>👤 Requested by</span>
                            <span style={{ fontSize: 11, fontWeight: 600 }}>{active.requesterName}</span>
                            {active.requesterRole ? <span style={{ fontSize: 10, color: 'var(--muted)' }}>{active.requesterRole}</span> : null}
                            {active.requesterCustomer ? <span style={{ fontSize: 10, color: 'var(--muted)' }}>· {active.requesterCustomer}</span> : null}
                            {active.requesterEmail ? <a href={`mailto:${active.requesterEmail}`} style={{ fontSize: 10, color: 'var(--info)' }}>{active.requesterEmail}</a> : null}
                            {active.requesterPhone ? (
                              <a
                                href={waLink(active.requesterPhone, `Hi ${active.requesterName.split(' ')[0]}, re the vacancy for ${active.role} at ${active.projectName ?? active.projectId}. Any updates?`)}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: 10, color: '#25D366' }}
                              >💬 WhatsApp</a>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        {canCreateVacancy ? <button className="btn btn-ghost btn-xs" title="Re-run match against current worker availability" onClick={() => rerun.mutate(active.id)} disabled={rerun.isPending}>{rerun.isPending ? '…' : '↺ Re-run'}</button> : null}
                        <button className="close-btn" onClick={() => setActiveId(null)}>×</button>
                      </div>
                    </div>
                    {active.candidates.length === 0 ? (
                      <div style={{ padding: 20 }}>
                        <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', marginBottom: 12 }}>No workers match the selected criteria</div>
                        {active.diagnostics ? (() => {
                          const d = active.diagnostics;
                          const matchingRole = d.totalWorkers - d.wrongRole;
                          const missingEntries = Object.entries(d.missingRequiredSkills);
                          return (
                            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, padding: 11, fontSize: 11, color: 'var(--muted)' }}>
                              <div style={{ fontWeight: 600, marginBottom: 6 }}>Why?</div>
                              <div>{d.totalWorkers} worker{d.totalWorkers === 1 ? '' : 's'} checked → {d.wrongRole} wrong role → {matchingRole} {active.role}{matchingRole === 1 ? '' : 's'}{missingEntries.length ? ' missing required skill(s):' : ''}</div>
                              {missingEntries.map(([skill, names]) => (
                                <div key={skill} style={{ marginTop: 5, paddingLeft: 8 }}>
                                  <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{skill}:</span>{' '}
                                  <span style={{ color: 'var(--muted)' }}>{names.slice(0, 5).join('; ')}{names.length > 5 ? `; +${names.length - 5} more` : ''}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })() : null}
                      </div>
                    ) : (
                      active.candidates.map((w, i) => {
                        const rolesLine = (w.workerRoles && w.workerRoles.length ? w.workerRoles : [w.workerRole]).filter(Boolean).join(' · ');
                        const waMsg = `Hi ${w.workerName.split(' ')[0]}, we have a vacancy: ${active.role} at ${active.projectName ?? active.projectId}. Are you available?`;
                        return (
                          <div key={w.workerId} className="vac-card">
                            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                              <div className="vac-rank" style={{ background: w.status === 'available' ? 'var(--live)' : w.status === 'deployed' ? 'var(--accent)' : 'var(--danger)' }}>{i + 1}</div>
                              {avEl(w.workerName, 34, 12)}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 12 }}>{w.workerName}</div>
                                <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 3 }}>
                                  {rolesLine}{w.workerPostcode ? ` · 📍${w.workerPostcode}` : ''} · {w.distanceKm.toFixed(1)}km
                                </div>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                                  {stBdg(w.status)}
                                  {w.onSiteUntil && w.status !== 'available' ? <span style={{ fontSize: 9, color: 'var(--muted)' }}>until {w.onSiteUntil}</span> : null}
                                  <span className={`badge ${w.score > 70 ? 'b-valid' : w.score > 40 ? 'b-expiring' : 'b-expired'}`} style={{ fontSize: 9 }}>{w.score}%</span>
                                </div>
                                <div className="vac-score-bar">
                                  <div className="vac-score-fill" style={{ width: `${w.score}%`, background: w.score > 70 ? 'var(--live)' : w.score > 40 ? 'var(--warn)' : 'var(--danger)' }}></div>
                                </div>
                                <div style={{ marginTop: 4, fontSize: 9, color: 'var(--muted)', lineHeight: 1.5 }}>
                                  {(w.details && w.details.length ? w.details : w.matchingCerts).slice(0, 4).join(' · ')}
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                                {w.workerPhone ? (
                                  <a className="btn btn-wa btn-xs" href={waLink(w.workerPhone, waMsg)} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><WaIcon size={11} /></a>
                                ) : null}
                                {canDeploy ? <button className="btn btn-primary btn-xs" onClick={() => setDeployTarget({ workerId: w.workerId, workerName: w.workerName, workerPhone: w.workerPhone ?? '', projectId: active.projectId, projectName: active.projectName ?? active.projectId, vacancyId: active.id })}>Deploy</button> : null}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    {active.unavailableCandidates && active.unavailableCandidates.length > 0 ? (
                      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
                        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--danger)', marginBottom: 9 }}>⚠ Unavailable Workers ({active.unavailableCandidates.length}, shown for reference)</div>
                        {active.unavailableCandidates.map((w) => (
                          <div key={w.workerId} className="vac-card" style={{ opacity: .65, borderColor: 'rgba(239,68,68,.2)' }}>
                            <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
                              {avEl(w.workerName, 28, 10)}
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, fontWeight: 500 }}>{w.workerName}</div>
                                <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                                  {w.workerRole ?? ''}{w.onSiteUntil ? ` · On site until ${w.onSiteUntil}` : ''}
                                </div>
                              </div>
                              <span className="badge b-unavail">unavailable</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                );
              })() : null}
            </div>
          </div>
        </div>
      </div>
      {addOpen ? <AddVacancyModal onClose={() => setAddOpen(false)} /> : null}
      {editingVacancyId ? (() => {
        const v = vac.open.find((x) => x.id === editingVacancyId) ?? vac.filled.find((x) => x.id === editingVacancyId);
        if (!v) return null;
        return (
          <AddVacancyModal
            onClose={() => setEditingVacancyId(null)}
            editing={{
              id: v.id,
              projectId: v.projectId,
              role: v.role,
              startDate: v.startDate ?? null,
              duration: v.duration ?? null,
              requiredSkills: v.requiredSkills ?? [],
              statusFilter: v.statusFilter ?? [],
              requesterId: v.requesterId ?? null,
              notes: v.notes ?? null,
            }}
          />
        );
      })() : null}
      {deployTarget ? <DeployModal onClose={() => setDeployTarget(null)} {...deployTarget} /> : null}
    </div>
  );
}
